import type { AIContext, AIContextOverrides } from '@/pages/journeys/settings/ai-business-profile/types';
import { INDUSTRIES, CUSTOM_INDUSTRY_OPTION_VALUE } from './constants';
import { isEmptyString } from '@/utils/string';

export interface FormData {
  name: string;
  websiteUrl: string;
  industry: string;
  customIndustry: string;
  longDescription: string;
  audience: string;
  formalityScore: number;
  humorScore: number;
  respectScore: number;
  energyScore: number;
  privacyPolicyUrl: string;
  termsOfServiceUrl: string;
  pricingUrl: string;
  downloadUrl: string;
  iosUrl: string;
  androidUrl: string;
  macUrl: string;
  windowsUrl: string;
  webUrl: string;
  browserExtensionUrl: string;
  apiUrl: string;
}

export const toFormData = (aiContext?: AIContext, overrides?: AIContextOverrides): FormData => {
  const industry = overrides?.industry ?? aiContext?.industry ?? '';
  const isIndustryCustom = !INDUSTRIES.includes(industry as (typeof INDUSTRIES)[number]) && !isEmptyString(industry);

  // Reverse formality and respect scores when loading from backend to match new UI labels
  // Backend stores: Formal (1) <- -> Casual (5), but UI shows: Casual (1) <- -> Formal (5)
  // Clamp to 1-5 range to handle legacy data that may have 0 values
  const rawFormalityScore = overrides?.tone?.formality?.score ?? aiContext?.tone?.formality?.score ?? 1;
  const rawRespectScore = overrides?.tone?.respect?.score ?? aiContext?.tone?.respect?.score ?? 1;
  const backendFormalityScore = Math.max(1, Math.min(5, rawFormalityScore));
  const backendRespectScore = Math.max(1, Math.min(5, rawRespectScore));
  const uiFormalityScore = 6 - backendFormalityScore;
  const uiRespectScore = 6 - backendRespectScore;

  return {
    name: overrides?.name ?? aiContext?.name ?? '',
    websiteUrl: overrides?.website_url ?? aiContext?.website_url ?? '',
    customIndustry: isIndustryCustom ? industry : '',
    industry: isIndustryCustom ? CUSTOM_INDUSTRY_OPTION_VALUE : industry,
    longDescription: overrides?.long_description ?? aiContext?.long_description ?? '',
    audience: overrides?.audience ?? aiContext?.audience ?? '',
    formalityScore: uiFormalityScore,
    humorScore: Math.max(1, Math.min(5, overrides?.tone?.humor?.score ?? aiContext?.tone?.humor?.score ?? 1)),
    respectScore: uiRespectScore,
    energyScore: Math.max(1, Math.min(5, overrides?.tone?.energy?.score ?? aiContext?.tone?.energy?.score ?? 1)),
    privacyPolicyUrl: overrides?.privacy_policy_url ?? aiContext?.privacy_policy_url ?? '',
    termsOfServiceUrl: overrides?.terms_of_service_url ?? aiContext?.terms_of_service_url ?? '',
    pricingUrl: overrides?.pricing_url ?? aiContext?.pricing_url ?? '',
    downloadUrl: overrides?.download_url ?? aiContext?.download_url ?? '',
    iosUrl: overrides?.platforms?.ios?.link ?? aiContext?.platforms?.ios?.link ?? '',
    androidUrl: overrides?.platforms?.android?.link ?? aiContext?.platforms?.android?.link ?? '',
    macUrl: overrides?.platforms?.mac?.link ?? aiContext?.platforms?.mac?.link ?? '',
    windowsUrl: overrides?.platforms?.windows?.link ?? aiContext?.platforms?.windows?.link ?? '',
    webUrl: overrides?.platforms?.web?.link ?? aiContext?.platforms?.web?.link ?? '',
    browserExtensionUrl:
      overrides?.platforms?.browserExtension?.link ?? aiContext?.platforms?.browserExtension?.link ?? '',
    apiUrl: overrides?.platforms?.api?.link ?? aiContext?.platforms?.api?.link ?? '',
  };
};
